/**
 * useSessionSettings — per-research-session jurisdiction / output language /
 * sources, resolved by the backend.
 *
 * `GET research/chat-groups/{uuid}/settings` returns the EFFECTIVE values
 * (session override → the user's AI settings → platform default) plus a
 * `*_source` per field saying which layer answered, and the raw `overrides`
 * (null = inherited). The UI never re-implements that resolution: it shows
 * what the backend resolved and writes overrides back.
 *
 * Write semantics, straight from `set_session_settings`:
 *   • omit a key   → leave that setting untouched
 *   • send `null`  → CLEAR the override, back to AI settings
 * so `clearJurisdiction()` and `setJurisdiction(code)` are different calls,
 * not the same call with an empty string.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store';
import {
  ResearchEffectiveSettings,
  ResearchSessionSettings,
  ResearchSettingsPatch,
  ResearchSource,
  researchGetSessionSettings,
  researchSetSessionSettings,
} from '../../store/actions/ChatAction';
import { toSourceSelection } from './sources';

/** What a session falls back to before anything has loaded. */
export const EMPTY_EFFECTIVE: ResearchEffectiveSettings = {
  jurisdiction: null,
  jurisdiction_source: 'none',
  output_language: '',
  output_language_source: 'none',
  sources: [],
  sources_source: 'none',
};

export interface UseSessionSettingsResult {
  effective: ResearchEffectiveSettings;
  overrides: ResearchSessionSettings['overrides'];
  isLoading: boolean;
  isSaving: boolean;
  /** True once a chat is open and its settings have been read. */
  isReady: boolean;
  setJurisdiction: (code: string) => Promise<void>;
  clearJurisdiction: () => Promise<void>;
  setOutputLanguage: (language: string) => Promise<void>;
  clearOutputLanguage: () => Promise<void>;
  setSources: (sources: ResearchSource[]) => Promise<void>;
  /** Apply several changes in one request. */
  patch: (patch: ResearchSettingsPatch) => Promise<void>;
  reload: () => void;
}

const NO_OVERRIDES: ResearchSessionSettings['overrides'] = {
  jurisdiction: null,
  output_language: null,
};

export function useSessionSettings(chatUuid: string | null): UseSessionSettingsResult {
  const dispatch = useAppDispatch();
  const [settings, setSettings] = useState<ResearchSessionSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Guards a response from a chat the user has already navigated away from.
  const activeUuidRef = useRef<string | null>(null);
  activeUuidRef.current = chatUuid;

  /**
   * Guards a response from an OLDER request for the SAME chat.
   *
   * The read fires the moment a chat exists, which on a freshly created chat
   * is while the first send is still writing the scope. That read answers
   * with the pre-write state, and if it lands after the reload that follows
   * the write it would put the empty settings back — the jurisdiction picked
   * on the start screen disappearing a beat after the URL changed. Only the
   * newest request may write state.
   */
  const requestSeq = useRef(0);

  useEffect(() => {
    if (!chatUuid) {
      setSettings(null);
      return;
    }
    let cancelled = false;
    const seq = ++requestSeq.current;
    setIsLoading(true);
    dispatch(researchGetSessionSettings(chatUuid))
      .then((result) => {
        if (cancelled || activeUuidRef.current !== chatUuid) return;
        if (seq !== requestSeq.current) return;
        setSettings(result);
      })
      .catch(() => {
        // A failed read leaves the composer on its defaults rather than
        // blocking the chat — the backend still resolves settings per query.
        if (!cancelled) setSettings(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [chatUuid, dispatch, reloadToken]);

  const applyPatch = useCallback(
    async (body: ResearchSettingsPatch) => {
      const uuid = activeUuidRef.current;
      if (!uuid) return;
      setIsSaving(true);
      const seq = ++requestSeq.current;
      try {
        const result = await dispatch(researchSetSessionSettings(uuid, body));
        // The PUT returns the recomputed effective settings — adopt them
        // rather than guessing what the override resolved to.
        if (activeUuidRef.current === uuid && seq === requestSeq.current) setSettings(result);
      } catch {
        // handleApiError already surfaced it; keep the last known good state.
      } finally {
        setIsSaving(false);
      }
    },
    [dispatch]
  );

  return {
    effective: settings?.effective ?? EMPTY_EFFECTIVE,
    overrides: settings?.overrides ?? NO_OVERRIDES,
    isLoading,
    isSaving,
    isReady: !!settings,
    setJurisdiction: (code: string) => applyPatch({ jurisdiction: code }),
    clearJurisdiction: () => applyPatch({ jurisdiction: null }),
    setOutputLanguage: (language: string) => applyPatch({ output_language: language }),
    clearOutputLanguage: () => applyPatch({ output_language: null }),
    setSources: (sources: ResearchSource[]) =>
      applyPatch({ sources: toSourceSelection(sources) }),
    patch: applyPatch,
    reload: () => setReloadToken((n) => n + 1),
  };
}

export default useSessionSettings;
